import { useEffect, useMemo, useState } from "react";

import moment from "moment";

// serializer
import VoucherAvailableSerializer from "issara-sdk/types/VoucherAvailableSerializer_apps_BIL";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import ModCancelExpense from "react-lib/apps/HISV3/DFC/ModCancelExpense";
import { formatDate, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ChipV4 } from "../common/ui";

import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type CancelData,
  type InvoiceDiscount,
  type PickedState,
  type ReceiptData,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/BillingV4";
import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  categoryCountsOf,
  categoryOf,
  coveredOf,
  dueOf,
  emptyFilter,
  encounterOptionsOf,
  filterExceptCategory,
  filterItems,
  packageApplyDataOf,
  packageFlashOf,
  PAY_FILTER_OPTIONS,
  payButtonText,
  payStatusLabel,
  payStatusOf,
  totalOf,
  visibleItemsOf,
  type BillingCategory,
  type BillingFilter,
  type InvoiceItem,
  type PackageDraft,
  type PayFilter,
} from "./billingModelV4";
import { payFlashOf } from "./billingPayModelV4";
import ModalBillingPackageV4 from "./ModalBillingPackageV4";
import ModalBillingPayV4 from "./ModalBillingPayV4";

// Types
type BillingPendingTabV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  BillingV4Sequence?: State["BillingV4Sequence"];
  patientId?: number;
  patient?: {
    hn?: string | null;
    full_name?: string;
    real_name?: string;
    real_name_th?: string;
    real_name_en?: string;
  } | null;
  masterOptions?: Record<string, any>;
  selectedDivision?: Record<string, any> | null;
  // callback
  runSequence: RunSequence;
  setProp?: SetProp;
  onGoOrders?: () => void;
};

// Consts
const ERROR_KEYS = [BTN_ACTS.LOAD, BTN_ACTS.DELETE, BTN_ACTS.PAY];

/** แท็บรายการรอรับชำระเงิน: ตัวกรอง ตารางค่าใช้จ่าย ยอดรวม และปุ่มรับชำระ */
const BillingPendingTabV4 = (props: BillingPendingTabV4Props) => {
  const [filter, setFilter] = useState<BillingFilter>(() => emptyFilter(formatDate(moment())));
  const [unchecked, setUnchecked] = useState<InvoiceItem["id"][]>([]);
  const [flash, setFlash] = useState("");
  const [packageOf, setPackageOf] = useState<InvoiceItem | null>(null);
  const [deleteOf, setDeleteOf] = useState<InvoiceItem | null>(null);

  const seq = props.BillingV4Sequence;
  const loading =
    seq?.patientId !== props.patientId ||
    props.buttonLoadCheck?.[BTN_ACTS.LOAD] === BLC_STATUS.LOADING;
  const packageError =
    props.errorMessage?.[BTN_ACTS.PKG_APPLY] || props.errorMessage?.[BTN_ACTS.PKG_LOAD];
  const payLoading = props.buttonLoadCheck?.[BTN_ACTS.PAY] === BLC_STATUS.LOADING;

  // Memo
  const items = useMemo(() => visibleItemsOf(seq?.items ?? []), [seq?.items]);
  const encounterOptions = useMemo(() => encounterOptionsOf(items), [items]);
  const chipItems = useMemo(() => filterExceptCategory(items, filter), [items, filter]);
  const chips = useMemo(() => categoryCountsOf(chipItems), [chipItems]);
  const rows = useMemo(() => filterItems(items, filter), [items, filter]);
  const selectable = rows.filter((item) => payStatusOf(item) !== "paid");
  const selected = selectable.filter((item) => !unchecked.includes(item.id));
  const allSelected = selectable.length > 0 && selected.length === selectable.length;

  // Effect
  useEffect(() => {
    if (!props.patientId) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patientId,
    });

    setFilter(emptyFilter(formatDate(moment())));
    setUnchecked([]);
    setFlash("");

    for (const key of [BTN_ACTS.DELETE, BTN_ACTS.PKG_LOAD, BTN_ACTS.PKG_APPLY, BTN_ACTS.PAY]) {
      if (props.errorMessage?.[key]) {
        props.setProp?.(`errorMessage.${key}`, null);
      }
    }
  }, [props.patientId]);

  // Handler
  const handleChangeFilter = <K extends keyof BillingFilter>(key: K, value: BillingFilter[K]) => {
    setFilter({ ...filter, [key]: value });
  };

  const handleChangeEncounter = (value: string) => {
    setFilter({ ...filter, encounter: value, useEncounter: true });
  };

  const handleSearch = () => {
    if (!props.patientId) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patientId,
    });
  };

  const handleClear = () => {
    setFilter({
      ...filter,
      useDate: false,
      useEncounter: false,
      pay: "all",
      q: "",
      category: "all",
    });
  };

  const handleToggle = (item: InvoiceItem, checked: boolean) => {
    setUnchecked(checked ? unchecked.filter((id) => id !== item.id) : [...unchecked, item.id]);
  };

  const handleToggleAll = (checked: boolean) => {
    const ids = selectable.map((item) => item.id);
    setUnchecked(checked ? unchecked.filter((id) => !ids.includes(id)) : [...unchecked, ...ids]);
  };

  const handleOpenPackage = (item: InvoiceItem) => {
    if (payStatusOf(item) === "paid") {
      return;
    }

    setPackageOf(item);
    props.setProp?.(`errorMessage.${BTN_ACTS.PKG_APPLY}`, null);
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PKG_LOAD, item });
  };

  const handleClosePackage = () => {
    setPackageOf(null);
  };

  const handleClosePackageError = () => {
    props.setProp?.(`errorMessage.${BTN_ACTS.PKG_APPLY}`, null);
    props.setProp?.(`errorMessage.${BTN_ACTS.PKG_LOAD}`, null);
  };

  const handleSavePackage = (draft: PackageDraft) => {
    if (!packageOf) {
      return;
    }

    const item = packageOf;
    const rowsOfItem = seq?.packageRows ?? [];

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PKG_APPLY,
      item,
      data: packageApplyDataOf(rowsOfItem, draft),
      onDone: () => {
        setPackageOf(null);
        setFlash(packageFlashOf(item, rowsOfItem, draft));
      },
    });
  };

  const handleOpenDelete = (item: InvoiceItem) => {
    setDeleteOf(item);
  };

  const handleCloseDelete = () => {
    setDeleteOf(null);
  };

  const handleDelete = (data: CancelData) => {
    if (!deleteOf) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.DELETE,
      item: deleteOf,
      data: {
        division: data.division,
        password: data.password,
        remark: data.remark,
        username: data.username,
      },
      onDone: () => setDeleteOf(null),
    });
  };

  const handlePrintPending = () => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PRINT_PENDING,
      items: items.filter((item) => payStatusOf(item) !== "paid"),
    });
  };

  const handlePay = () => {
    if (selected.length === 0) {
      return;
    }

    setFlash("");
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PAY, items: selected });
  };

  const handlePayCalc = (invoiceDiscounts: InvoiceDiscount[], vouchers: number[]) => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PAY_CALC,
      invoiceDiscounts,
      vouchers,
    });
  };

  const handleCheckVoucher = (
    code: string,
    onDone: (voucher: VoucherAvailableSerializer | null) => void,
  ) => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PAY_VOUCHER, code, onDone });
  };

  const handleCheckCoupon = (code: string, onDone: (value: string | null) => void) => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PAY_COUPON, code, onDone });
  };

  const handleCheckAffiliate = (
    code: string,
    onDone: (affiliate: { id: number; name: string } | null) => void,
  ) => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PAY_AFFILIATE, code, onDone });
  };

  const handlePaySubmit = (data: ReceiptData, receiptNo?: string) => {
    const items = seq?.pay?.items ?? [];
    const due = Number(data.price) || 0;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.PAY_SUBMIT,
      data,
      receiptNo,
      onDone: (receiptCode) => {
        setUnchecked([]);
        setFlash(payFlashOf(items, due, receiptCode));
      },
    });
  };

  const handleClosePay = () => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.PAY_CLOSE });
  };

  const handleClosePayError = (key: "calc" | "submit") => {
    props.setProp?.(
      `errorMessage.${key === "calc" ? BTN_ACTS.PAY_CALC : BTN_ACTS.PAY_SUBMIT}`,
      null,
    );
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  // Render
  const renderCategory = (category: BillingCategory) => {
    const tone = CATEGORY_TONES[category];
    return tone ? (
      <ChipV4 tone={tone} size="sm">
        {CATEGORY_LABELS[category]}
      </ChipV4>
    ) : (
      <span className="hv4-chip hv4-chip-sm hv4-bill-cat-package">{CATEGORY_LABELS[category]}</span>
    );
  };

  const renderPackageButton = (item: InvoiceItem) => {
    if (item.package_status !== "AVAILABLE" && item.package_status !== "APPLIED") {
      return null;
    }

    const applied = item.package_status === "APPLIED";
    const name = seq?.packageNames?.[item.id ?? 0];
    const paid = payStatusOf(item) === "paid";
    const title = applied
      ? `ตัดใช้ Package: ${name || "-"}${paid ? "" : " · กดเพื่อเปลี่ยน"}`
      : "มี Package ที่ใช้รายการนี้ได้ · กดเพื่อเลือกตัดใช้";

    return (
      <button
        type="button"
        className={`hv4-bill-pkgbtn${applied ? " hv4-bill-pkgbtn--on" : ""}${paid ? " hv4-bill-pkgbtn--lock" : ""}`}
        aria-label={title}
        title={title}
        aria-disabled={paid || undefined}
        onClick={() => handleOpenPackage(item)}
      >
        Package
      </button>
    );
  };

  const renderRow = (item: InvoiceItem) => {
    const status = payStatusOf(item);
    const paid = status === "paid";
    const covered = coveredOf(item);
    const name = seq?.packageNames?.[item.id ?? 0];
    const cancelable = (seq?.cancelableModes ?? []).includes(item.mode_code);
    const deleteTitle = paid
      ? "ลบไม่ได้ · ชำระเงินแล้ว"
      : cancelable
        ? "ลบรายการ"
        : "ลบไม่ได้ · หมวดค่าใช้จ่ายนี้ยกเลิกไม่ได้";

    return (
      <div key={item.id} className="hv4-bill-grid hv4-bill-tr" role="row">
        <span className="hv4-bill-td-chk">
          <input
            type="checkbox"
            className="hv4-bill-chk"
            aria-label={`เลือก ${item.product}`}
            disabled={paid}
            checked={!paid && !unchecked.includes(item.id)}
            onChange={(e) => handleToggle(item, e.target.checked)}
          />
        </span>
        <span className="hv4-bill-td-c">{item.encounter_number}</span>
        <span className="hv4-bill-td-c">
          <span className={`hv4-bill-pill hv4-bill-pill--${status}`}>{payStatusLabel(item)}</span>
        </span>
        <span className="hv4-bill-td">{item.code}</span>
        <span className="hv4-bill-td">{renderCategory(categoryOf(item))}</span>
        <span className="hv4-bill-td">
          <span className="hv4-bill-name">
            {item.product}
            {renderPackageButton(item)}
          </span>
          {status === "package" && !!name && (
            <span className="hv4-bill-name-sub">ใช้สิทธิ์ {name}</span>
          )}
          {paid && item.package_status === "APPLIED" && !!name && (
            <span className="hv4-bill-name-sub">ตัดสิทธิ์จาก {name} แล้ว</span>
          )}
        </span>
        <span className="hv4-bill-td">—</span>
        <span className="hv4-bill-td-c">{formatISOtoBEDateTime(item.created).dateTimeShort}</span>
        <span className="hv4-bill-td-r">{formatComma(Number(item.quantity) || 0)}</span>
        <span className="hv4-bill-td-r">{formatComma(Number(item.unitprice) || 0)}</span>
        <span className="hv4-bill-td-r">{formatComma(Number(item.price) || 0)}</span>
        <span
          className={`hv4-bill-td-r${covered > 0 ? " hv4-bill-covered" : " hv4-bill-covered--0"}`}
        >
          {covered > 0 ? `− ${formatComma(covered)}` : "0.00"}
        </span>
        <span className="hv4-bill-td-r">{formatComma(dueOf(item))}</span>
        <span className="hv4-bill-td-c">
          <button
            type="button"
            className="hv4-bill-del"
            aria-label={`ลบ ${item.product}`}
            title={deleteTitle}
            disabled={paid || !cancelable}
            onClick={() => handleOpenDelete(item)}
          >
            −
          </button>
        </span>
      </div>
    );
  };

  return (
    <div className="hv4-bill-pending">
      {ERROR_KEYS.map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => handleCloseError(key)}
        />
      ))}

      <section className="hv4-card hv4-bill-card">
        <div className="hv4-bill-card-head">
          <h2 className="hv4-card-title hv4-bill-ch">รายการรอรับชำระ</h2>
        </div>

        <div className="hv4-bill-filter">
          <label className="hv4-bill-check">
            <input
              type="checkbox"
              className="hv4-bill-chk"
              checked={filter.useDate}
              onChange={(e) => handleChangeFilter("useDate", e.target.checked)}
            />
            วันที่ค่าใช้จ่าย
          </label>
          <div className="hv4-date hv4-bill-w150" aria-label="วันที่เริ่มต้น">
            <DateTextBox
              placeholder="DD/MM/YYYY"
              value={filter.startDate}
              onChange={(date: string) => handleChangeFilter("startDate", date)}
            />
          </div>
          <div className="hv4-date hv4-bill-w150" aria-label="วันที่สิ้นสุด">
            <DateTextBox
              placeholder="DD/MM/YYYY"
              value={filter.endDate}
              onChange={(date: string) => handleChangeFilter("endDate", date)}
            />
          </div>
          <label className="hv4-bill-check">
            <input
              type="checkbox"
              className="hv4-bill-chk"
              checked={filter.useEncounter}
              onChange={(e) => handleChangeFilter("useEncounter", e.target.checked)}
            />
            เลขรับบริการ
          </label>
          <select
            className="hv4-input hv4-bill-w150"
            aria-label="เลขรับบริการ"
            value={filter.encounter}
            onChange={(e) => handleChangeEncounter(e.target.value)}
          >
            <option value="">-</option>
            {encounterOptions.map((number) => (
              <option key={number} value={number}>
                {number}
              </option>
            ))}
          </select>
        </div>

        <div className="hv4-bill-filter hv4-bill-filter--end">
          <div>
            <label className="hv4-label" htmlFor="hv4-bill-pay">
              การชำระเงิน
            </label>
            <select
              id="hv4-bill-pay"
              className="hv4-input hv4-bill-w190"
              value={filter.pay}
              onChange={(e) => handleChangeFilter("pay", e.target.value as PayFilter)}
            >
              {PAY_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="hv4-bill-q">
            <label className="hv4-label" htmlFor="hv4-bill-q">
              Package / รายการตรวจ
            </label>
            <input
              id="hv4-bill-q"
              className="hv4-input"
              placeholder="ค้นหาชื่อแพ็กเกจ / รายการตรวจ / รหัส"
              value={filter.q}
              onChange={(e) => handleChangeFilter("q", e.target.value)}
            />
          </div>
          <button type="button" className="hv4-btn hv4-bill-btn-brand" onClick={handleSearch}>
            ค้นหา
          </button>
          <button type="button" className="hv4-btn hv4-btn-ghost" onClick={handleClear}>
            ล้างตัวกรอง
          </button>
        </div>

        <div className="hv4-bill-chips" role="group" aria-label="กรองตามหมวด">
          <button
            type="button"
            className={`hv4-bill-chip${filter.category === "all" ? " hv4-bill-chip--on" : ""}`}
            aria-pressed={filter.category === "all"}
            onClick={() => handleChangeFilter("category", "all")}
          >
            ทั้งหมด ({chipItems.length})
          </button>
          {chips.map((chip) => (
            <button
              key={chip.category}
              type="button"
              className={`hv4-bill-chip${filter.category === chip.category ? " hv4-bill-chip--on" : ""}`}
              aria-pressed={filter.category === chip.category}
              onClick={() => handleChangeFilter("category", chip.category)}
            >
              {CATEGORY_LABELS[chip.category]} ({chip.count})
            </button>
          ))}
        </div>

        {!!flash && (
          <div role="status" className="hv4-bill-flash">
            {flash}
          </div>
        )}

        <div
          className="hv4-bill-table"
          role="table"
          aria-label="รายการรอรับชำระ"
          aria-busy={loading}
        >
          <div className="hv4-bill-inner">
            <div className="hv4-bill-grid hv4-bill-th" role="row">
              <span className="hv4-bill-td-chk">
                <input
                  type="checkbox"
                  className="hv4-bill-chk"
                  aria-label="เลือกทั้งหมด"
                  disabled={selectable.length === 0}
                  checked={allSelected}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                />
              </span>
              <span>เลขรับบริการ</span>
              <span>สถานะการเงิน</span>
              <span>รหัส</span>
              <span>หมวด</span>
              <span>รายการค่าบริการ (EN)</span>
              <span>รายการค่าบริการ (TH)</span>
              <span>วันที่เวลาค่าใช้จ่าย</span>
              <span>จำนวน</span>
              <span>ราคาต่อหน่วย</span>
              <span>ราคารวม</span>
              <span title="ยอดที่สิทธิรับผิดชอบ ทั้งส่งเบิก (ประกัน / สวัสดิการ) และส่วนลดตามสิทธิ">
                ส่วนลด / เบิกจากสิทธิ
              </span>
              <span>ยอดชำระ</span>
              <span>ลบ</span>
            </div>
            {rows.map(renderRow)}
            {rows.length === 0 && (
              <div className="hv4-bill-empty">
                {loading ? "กำลังโหลดรายการ" : "ไม่พบรายการตามตัวกรอง"}
              </div>
            )}
          </div>
        </div>

        <div className="hv4-bill-foot">
          <div className="hv4-bill-total">
            <b>ยอดรวมทั้งหมด</b>
            <span className="hv4-bill-total-box">{formatComma(totalOf(selected))}</span>
          </div>
          <span className="hv4-bill-count">เลือก {selected.length} รายการ</span>
          <button type="button" className="hv4-btn hv4-bill-btn-grey" onClick={handlePrintPending}>
            พิมพ์รายการค้างชำระ
          </button>
          <button
            type="button"
            className="hv4-btn hv4-bill-btn-brand"
            disabled={selected.length === 0 || payLoading}
            onClick={handlePay}
          >
            {payButtonText(selected)}
          </button>
          <button type="button" className="hv4-bill-link" onClick={props.onGoOrders}>
            ไปแก้ไขที่หน้าคำสั่ง
          </button>
        </div>
      </section>

      {!!packageOf && (
        <ModalBillingPackageV4
          item={packageOf}
          rows={seq?.packageItemId === packageOf.id ? (seq?.packageRows ?? []) : []}
          loading={props.buttonLoadCheck?.[BTN_ACTS.PKG_LOAD] === BLC_STATUS.LOADING}
          saving={props.buttonLoadCheck?.[BTN_ACTS.PKG_APPLY] === BLC_STATUS.LOADING}
          error={packageError}
          onSave={handleSavePackage}
          onCloseError={handleClosePackageError}
          onClose={handleClosePackage}
        />
      )}

      {!!seq?.pay && (
        <ModalBillingPayV4
          pay={seq.pay}
          patient={props.patient}
          doctorOptions={props.masterOptions?.doctor}
          calcError={props.errorMessage?.[BTN_ACTS.PAY_CALC]}
          submitError={props.errorMessage?.[BTN_ACTS.PAY_SUBMIT]}
          submitting={props.buttonLoadCheck?.[BTN_ACTS.PAY_SUBMIT] === BLC_STATUS.LOADING}
          onCalc={handlePayCalc}
          onCheckVoucher={handleCheckVoucher}
          onCheckCoupon={handleCheckCoupon}
          onCheckAffiliate={handleCheckAffiliate}
          onSubmit={handlePaySubmit}
          onClose={handleClosePay}
          onCloseError={handleClosePayError}
        />
      )}

      <ModCancelExpense
        open={!!deleteOf}
        buttonLoadCheck={props.buttonLoadCheck}
        selectedDivision={props.selectedDivision ?? undefined}
        divisionOptions={props.masterOptions?.division}
        options={props.masterOptions?.invoiceCancelNote}
        error={props.errorMessage?.[BTN_ACTS.DELETE]}
        isRequire
        isMeBilling
        onApprove={handleDelete}
        onClose={handleCloseDelete}
      />
    </div>
  );
};

BillingPendingTabV4.displayName = "BillingPendingTabV4";

export default BillingPendingTabV4;
